import clsx from 'clsx';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import IconButton from '@material-ui/core/IconButton';

import ChevronLeft from '@material-ui/icons/ChevronLeft';
import ChevronRight from '@material-ui/icons/ChevronRight';

import useStyles from './useStyles';

export const propTypes = {
  xs: PropTypes.number,
  sm: PropTypes.number,
  md: PropTypes.number,
  lg: PropTypes.number,
  xl: PropTypes.number,
  swipe: PropTypes.bool,
  spacing: PropTypes.number,
  showNavAlways: PropTypes.bool,
  showNavOnHover: PropTypes.bool,
  showNavOnHoverAll: PropTypes.bool,
};

export const defaultProps = {
  xs: 1,
  swipe: false,
  spacing: 3,
  showNavAlways: false,
  showNavOnHover: true,
  showNavOnHoverAll: false,
};

export default function Carousel(props) {
  const containerRef = useRef(null);
  const itemRef = useRef(null);

  const [dragging, setDragging] = useState(false);
  const [hasPrev, setHasPrev] = useState(false);
  const [hasNext, setHasNext] = useState(false);

  const { children, xs, sm, md, lg, xl, swipe, spacing, showNavAlways, showNavOnHover, showNavOnHoverAll } = props;
  const classes = useStyles({ xs, sm, md, lg, xl });

  const scrollBy = (x = 0, y = 0) => {
    containerRef.current.scrollBy({ top: y, left: x });
  };

  const scrollToPrev = () => {
    scrollBy(-itemRef.current.clientWidth);
  };

  const scrollToNext = () => {
    scrollBy(itemRef.current.clientWidth);
  };

  const handleScroll = useCallback(() => {
    const $target = containerRef.current;
    const scrollWidth = $target.scrollWidth - $target.clientWidth;
    setHasPrev($target.scrollLeft > 0);
    setHasNext(scrollWidth !== $target.scrollLeft);
  }, [containerRef]);

  useEffect(() => {
    handleScroll();
  }, [handleScroll]);

  const handleMouse = (e) => {
    const $target = containerRef.current;
    const { type, pageX } = e;

    if (type === 'mousedown') {
      setDragging({ initialScrollX: $target.scrollLeft, initialMouseX: pageX });
      return;
    }

    if (['mouseup', 'mouseleave'].includes(type)) {
      setDragging(null);
      return;
    }

    if (!dragging) {
      return;
    }

    let scroll = dragging.initialScrollX + dragging.initialMouseX - pageX;

    // Min 0
    scroll = Math.max(scroll, 0);

    // Max scroll
    scroll = Math.min(scroll, $target.scrollWidth);

    $target.scrollLeft = scroll;
  };

  return (
    <Box className={clsx(classes.root, showNavOnHoverAll && classes.showNavOnHoverAll, showNavAlways && classes.showNavAlways)}>
      <Grid
        ref={containerRef}
        container
        wrap="nowrap"
        className={clsx(classes.container, swipe && classes.swipe, dragging && 'scrolling')}
        spacing={spacing}
        onScroll={handleScroll}
        onMouseDown={swipe ? handleMouse : null}
        onMouseMove={swipe ? handleMouse : null}
        onMouseUp={swipe ? handleMouse : null}
        onMouseLeave={swipe ? handleMouse : null}
      >
        {children?.map((child, key) => (
          <Grid ref={itemRef} key={key} item className={classes.item}>
            {child}
          </Grid>
        ))}
      </Grid>
      {hasPrev && (
        <Box className={clsx(classes.button, classes.prev, showNavOnHover && classes.showNavOnHover)}>
          <IconButton onClick={scrollToPrev}>
            <ChevronLeft />
          </IconButton>
        </Box>
      )}
      {hasNext && (
        <Box className={clsx(classes.button, classes.next, showNavOnHover && classes.showNavOnHover)}>
          <IconButton onClick={scrollToNext}>
            <ChevronRight />
          </IconButton>
        </Box>
      )}
    </Box>
  );
}

Carousel.propTypes = propTypes;
Carousel.defaultProps = defaultProps;
